import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Form, Tag, Popconfirm, Space, Select } from 'antd';
import { Button } from 'components/buttons/buttons';
import { ContractorPaymentsApi } from 'api/api';
import { ContractorPaymentDto, ContractorProjectDetailsDto, PaymentMethod } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import dayjs from 'dayjs';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { CreateContractorPaymentModal } from './CreateContractorPaymentModal';
import openNotificationWithIcon from 'utility/notification';
import { UpdateContractorPaymentModal } from './UpdateContractorPaymentModal';
import renderPaymentMethodBadge from 'components/tags/renderPaymentMethodBadge';
import { TablePaginationConfig } from 'antd/lib/table/interface';

const contractorPaymentsApi = new ContractorPaymentsApi();

interface ContractorPaymentsTableProps {
  contractorId: number;
  onPaymentChange: () => void;
  projectId?: number;
  projects: ContractorProjectDetailsDto[];
}

export const ContractorPaymentsTable: React.FC<ContractorPaymentsTableProps> = ({
  contractorId,
  onPaymentChange,
  projectId,
  projects,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<ContractorPaymentDto[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<ContractorPaymentDto | null>(null);
  const [form] = Form.useForm();
  const [query, setQuery] = useState({
    pageNumber: 1,
    pageSize: 10,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    status: undefined as number | undefined,
    projectId: projectId,
  });

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);

  const fetchPayments = async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const response = await contractorPaymentsApi.apiContractorsContractorIdPaymentsGet({
        contractorId,
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        projectId: query.projectId,
      });

      setPayments(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
    setLoading(false);
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setQuery({
      ...query,
      pageNumber: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    });
  };

  useEffect(() => {
    fetchPayments();
  }, [query, contractorId]);

  useEffect(() => {
    setQuery(prev => ({
      ...prev,
      projectId: projectId === -1 ? undefined : projectId,
      pageNumber: 1,
    }));
  }, [projectId]);

  const handleCreatePayment = async (values: any) => {
    try {
      await contractorPaymentsApi.apiContractorsContractorIdPaymentsPost({
        contractorId,
        createContractorPaymentCommand: {
          ...values,
          contractorId,
          dueDate: values.dueDate.toISOString(),
          paidDate: values.paidDate.toISOString(),
        },
      });
      openNotificationWithIcon('success', t('payments:created', 'Payment created successfully'));
      setModalVisible(false);
      form.resetFields();
      fetchPayments();
      onPaymentChange();
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  const handleMarkAsPaid = async (record: ContractorPaymentDto) => {
    setMarkingPaidId(record.id!);
    try {
      await contractorPaymentsApi.apiContractorsContractorIdPaymentsIdMarkAsPaidPost({
        contractorId,
        id: record.id!,
        markContractorPaymentAsPaidCommand: {
          id: record.id!,
          paymentMethod: record.paymentMethod,
          transactionReference: record.transactionReference,
          paidDate: new Date().toISOString(),
        },
      });
      fetchPayments();
      onPaymentChange();
      openNotificationWithIcon(
        'success',
        t('payments:mark-as-paid', 'Contractor payment was marked as paid successfully'),
      );
    } catch (error) {
      console.error('Error marking payment as paid:', error);
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleUpdatePayment = async (values: any) => {
    try {
      await contractorPaymentsApi.apiContractorsContractorIdPaymentsIdPut({
        contractorId,
        id: selectedPayment!.id!,
        updateContractorPaymentCommand: {
          ...values,
          id: selectedPayment!.id!,
          contractorId,
          dueDate: values.dueDate.toISOString(),
        },
      });
      setUpdateModalVisible(false);
      setSelectedPayment(null);
      fetchPayments();
      onPaymentChange();
      openNotificationWithIcon('success', t('payments:update', 'Contractor payment updated successfully'));
    } catch (error) {
      console.error('Error updating payment:', error);
    }
  };

  const handleDeletePayment = async (id: number) => {
    try {
      await contractorPaymentsApi.apiContractorsContractorIdPaymentsIdDelete({
        contractorId,
        id,
      });
      fetchPayments();
      onPaymentChange();
      openNotificationWithIcon('success', t('payments:deleted', 'Contractor payment deleted successfully'));
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const showUpdateModal = (payment: ContractorPaymentDto) => {
    setSelectedPayment(payment);
    setUpdateModalVisible(true);
  };

  const columns = [
    {
      title: t('payments:project', 'Project'),
      dataIndex: 'projectName',
      render: (projectName: string, record: ContractorPaymentDto) => (
        <Link to={`/projects/${record.projectId}`}>{projectName}</Link>
      ),
    },
    {
      title: t('payments:amount', 'Amount'),
      dataIndex: 'amount',
      render: (amount: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount),
    },
    {
      title: t('payments:due-date', 'Due Date'),
      dataIndex: 'dueDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('payments:paid-date', 'Paid Date'),
      dataIndex: 'paidDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: t('payments:status', 'Status'),
      key: 'status',
      render: (record: ContractorPaymentDto) => (
        <Tag color={record.isPaid ? 'success' : record.isOverdue ? 'error' : 'warning'}>
          {record.isPaid
            ? t('payments:paid', 'Paid')
            : record.isOverdue
            ? t('payments:overdue', 'Overdue')
            : t('payments:pending', 'Pending')}
        </Tag>
      ),
    },
    {
      title: t('payments:note', 'Notes'),
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: t('payments:payment-method', 'Payment Method'),
      dataIndex: 'paymentMethod',
      render: (paymentMethod: PaymentMethod) => renderPaymentMethodBadge(paymentMethod),
    },
    {
      title: t('payments:transactionReference', 'Transaction Ref'),
      dataIndex: 'transactionReference',
      key: 'transactionReference',
    },
    {
      title: t('payments:actions', 'Actions'),
      key: 'actions',
      width: 150,
      align: 'center' as const,
      render: (record: ContractorPaymentDto) => (
        <>
          {!record.isPaid && hasPermission('contractors:manage-payments') && (
            <Space size="small">
              <Button
                type="primary"
                shape="circle"
                size="small"
                title={t('payments:mark-as-paid', 'Mark as Paid')}
                onClick={() => handleMarkAsPaid(record)}
                loading={markingPaidId === record.id}
                icon={
                  !markingPaidId || markingPaidId !== record.id ? (
                    <FeatherIcon icon="check-square" size={14} />
                  ) : (
                    undefined
                  )
                }
              />
              <Button
                type="primary"
                shape="circle"
                size="small"
                title={t('payments:edit', 'Edit Payment')}
                onClick={() => showUpdateModal(record)}
                icon={<FeatherIcon icon="edit" size={14} />}
              />
              <Popconfirm
                title={t('payments:delete-confirm', 'Are you sure you want to delete this payment?')}
                onConfirm={() => handleDeletePayment(record.id!)}
                okText={t('global:yes', 'Yes')}
                cancelText={t('global:no', 'No')}
              >
                <Button
                  type="primary"
                  danger
                  shape="circle"
                  size="small"
                  title={t('payments:delete', 'Delete Payment')}
                  icon={<FeatherIcon icon="trash-2" size={14} />}
                />
              </Popconfirm>
            </Space>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={payments}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
        loading={loading}
      />

      {/* Payment Creation Modal */}
      <CreateContractorPaymentModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleCreatePayment}
        projects={projects || []}
        onProjectSelect={() => {}}
      />

      {/* Payment Update Modal */}
      <UpdateContractorPaymentModal
        visible={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          setSelectedPayment(null);
        }}
        onSubmit={handleUpdatePayment}
        payment={selectedPayment}
        projects={projects || []}
        onProjectSelect={() => {}}
      />
    </>
  );
};
